import { type HTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "./utils/cn";

export interface NotesPostNeighbor {
  slug: string;
  title: string;
}

export interface NotesPostProps extends HTMLAttributes<HTMLElement> {
  number: string;
  title: string;
  byline: string;
  date: string;
  readingMinutes: number;
  signature: string;
  children: ReactNode;
  prev?: NotesPostNeighbor;
  next?: NotesPostNeighbor;
}

/**
 * Long-form post layout for the Field notes blog. The `children` slot is
 * styled minimally inside `.hui-notes-post__body` for the standard MDX
 * elements (h2, h3, p, ul, ol, a, pre, code, blockquote, hr). Anything more
 * structured than those should be passed through as @humaniti/ui primitives.
 */
export const NotesPost = ({
  number,
  title,
  byline,
  date,
  readingMinutes,
  signature,
  children,
  prev,
  next,
  className,
  ...rest
}: NotesPostProps) => (
  <article className={cn("hui-notes-post", className)} {...rest}>
    <header className="hui-notes-post__header">
      <span className="hui-notes-post__num">{number}</span>
      <h1 className="hui-notes-post__title">{title}</h1>
      <div className="hui-notes-post__meta">
        {byline} · {date} · {readingMinutes} min
      </div>
    </header>
    <div className="hui-notes-post__body">{children}</div>
    <footer className="hui-notes-post__footer">
      <p className="hui-notes-post__signature">{signature}</p>
      {(prev || next) && (
        <div className="hui-notes-post__nav">
          {prev ? (
            <Link to={`/notes/${prev.slug}`} className="hui-notes-post__nav-link">
              ← {prev.title}
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={`/notes/${next.slug}`} className="hui-notes-post__nav-link">
              {next.title} →
            </Link>
          ) : (
            <span />
          )}
        </div>
      )}
      <Link to="/notes" className="hui-notes-post__back">
        Back to Field notes
      </Link>
    </footer>
  </article>
);
